/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io'
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import KeywordDirectoryUpsert from 'components/KeywordDirectoryUpsert'
import KeywordDirectoryDropDownOptions from 'components/KeywordDirectoryDropDownOptions'
import KeywordDirectoryDelete from 'components/KeywordDirectoryDelete'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'


/*
 * GRAPHS
 */
import KeywordDirectoryReadQuery from './__query__/index.keywordDirectory.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [keywordDirectoryToSearch, setKeywordDirectoryToSearch] = React.useState('')
  const { 'isOpen': isKeywordDirectoryCreateOpen, 'onOpen': onKeywordDirectoryCreateOpen, 'onClose': onKeywordDirectoryCreateClose } = useDisclosure()
  const { 'isOpen': isKeywordDirectoryUpdateOpen, 'onOpen': onKeywordDirectoryUpdateOpen, 'onClose': onKeywordDirectoryUpdateClose } = useDisclosure()
  const { 'isOpen': isKeywordDirectoryDeleteOpen, 'onOpen': onKeywordDirectoryDeleteOpen, 'onClose': onKeywordDirectoryDeleteClose } = useDisclosure()

  const _QueryKeywordDirectoryRead = useQuery(KeywordDirectoryReadQuery, {
    'variables': {
      keywordDirectoryToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _onKeywordDirectoryToSearchInputChange = React.useRef(_.debounce((value) => setKeywordDirectoryToSearch(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data loading check.
  if (!_QueryKeywordDirectoryRead.loading && 0 < _QueryKeywordDirectoryRead.data?.KeywordDirectoryRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QueryKeywordDirectoryRead.data?.KeywordDirectoryRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onKeywordDirectoryToSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onKeywordDirectoryToSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 200
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'Display Name',
      'description': 'Display name of the directory.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 270
    },
    {
      'field': 'totalKeywords',
      'headerName': 'Total Keywords',
      'description': 'Total keywords count.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 260
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 260
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 260
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for directory.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 200,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <KeywordDirectoryDropDownOptions
        keywordDirectoryId={row?.id}
        onKeywordDirectoryUpdate={onKeywordDirectoryUpdateOpen}
        onKeywordDirectoryDelete={onKeywordDirectoryDeleteOpen}
      />
    }
  ], [onKeywordDirectoryUpdateOpen, onKeywordDirectoryDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryKeywordDirectoryRead?.data?.KeywordDirectoryRead?.map((r, index) =>
    _successFlags.includes(r.status) ? ({
      'id': r?.id || String.random(9),
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'displayName': r?.displayName ?? '-',
      'totalKeywords': r?._count?.Keyword ?? '-',
      'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
      'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
    }) : null
  )), [_QueryKeywordDirectoryRead?.data?.KeywordDirectoryRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='keywordDirectory base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between'>
            <Flex gap='12px'>
              <Tooltip label='Create New Folder' fontSize='sm'>
                <Button
                  leftIcon={<IoMdAdd />}
                  onClick={onKeywordDirectoryCreateOpen}
                  style={buttonStyle}>
                  Create New
                </Button>
              </Tooltip>
            </Flex>
            <DownloadToExcel
              cellsData={_QueryKeywordDirectoryRead?.data?.KeywordDirectoryRead?.map((item, __index) =>
                _successFlags.includes(item.status) ? ({
                  'S.No.': `${(__index + (paginationModel.pageSize * paginationModel.page)) + 1}.`,
                  'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                  'Display Name': item.displayName ?? '-',
                  'Total Keywords': item?._count?.Keyword ?? '-'
                }) : void 0
              )}
              headersData={[
                'S.No.',
                'CreatedAt',
                'UpdatedAt',
                'Display Name',
                'Total Keywords'
              ].map(i => ({ 'key': i, 'label': i }))}
            />
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryKeywordDirectoryRead?.data?.KeywordDirectoryRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[
              10,
              30,
              50,
              100
            ]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryKeywordDirectoryRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='Create Directory'
          isOpen={isKeywordDirectoryCreateOpen}
          onClose={onKeywordDirectoryCreateClose}>
          <KeywordDirectoryUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='Update Directory'
          isOpen={isKeywordDirectoryUpdateOpen}
          onClose={onKeywordDirectoryUpdateClose}>
          <KeywordDirectoryUpsert />
        </Modal>
        <Modal
          size='md'
          title='Delete Directory'
          isOpen={isKeywordDirectoryDeleteOpen}
          onClose={onKeywordDirectoryDeleteClose}>
          <KeywordDirectoryDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
